import { useRef, useEffect } from "react";
import type { Item, SourceSnapshot } from "@tuit/shared-types";
import { useViewSettings } from "../state/viewSettingsState.js";
import styles from "../styles/components/SourcePicturesView.module.css";

const THRESHOLD = 90;

interface CardProps {
  item: Item;
  isRead: boolean;
  onSkip: () => void;
}

function PictureCard({ item, isRead, onSkip }: CardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const skipBgRef = useRef<HTMLDivElement>(null);
  const touchState = useRef<{ startX: number; startY: number; dir: "h" | "v" | null }>({
    startX: 0,
    startY: 0,
    dir: null,
  });

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    function onStart(e: TouchEvent) {
      const t = e.touches[0];
      if (!t) return;
      touchState.current = { startX: t.clientX, startY: t.clientY, dir: null };
      const inner = innerRef.current;
      if (inner) inner.style.transition = "none";
    }

    function onMove(e: TouchEvent) {
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - touchState.current.startX;
      const dy = t.clientY - touchState.current.startY;
      const s = touchState.current;
      if (s.dir === null) {
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
        s.dir = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
      }
      if (s.dir !== "h") return;
      e.preventDefault();
      const inner = innerRef.current;
      if (!inner) return;
      inner.style.transform = `translateX(${dx}px)`;
      const skipBg = skipBgRef.current;
      if (skipBg) {
        skipBg.style.opacity = String(Math.min(1, Math.abs(dx) / THRESHOLD));
        skipBg.style.justifyContent = dx < 0 ? "flex-end" : "flex-start";
        skipBg.style.paddingLeft = dx > 0 ? "20px" : "0";
        skipBg.style.paddingRight = dx < 0 ? "20px" : "0";
      }
    }

    function onEnd() {
      const inner = innerRef.current;
      if (!inner || touchState.current.dir !== "h") return;
      const match = inner.style.transform.match(/-?[\d.]+/);
      const dx = match ? parseFloat(match[0]) : 0;
      inner.style.transition = "transform 0.25s ease";
      const skipBg = skipBgRef.current;
      if (Math.abs(dx) > THRESHOLD) {
        inner.style.transform = dx < 0 ? "translateX(-110%)" : "translateX(110%)";
        if (skipBg) {
          skipBg.style.transition = "opacity 0.25s ease";
          skipBg.style.opacity = "0";
        }
        setTimeout(onSkip, 240);
      } else {
        inner.style.transform = "translateX(0)";
        if (skipBg) {
          skipBg.style.transition = "opacity 0.2s ease";
          skipBg.style.opacity = "0";
        }
      }
      touchState.current.dir = null;
    }

    card.addEventListener("touchstart", onStart, { passive: true });
    card.addEventListener("touchmove", onMove, { passive: false });
    card.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      card.removeEventListener("touchstart", onStart);
      card.removeEventListener("touchmove", onMove);
      card.removeEventListener("touchend", onEnd);
    };
  }, [onSkip]);

  return (
    <div ref={cardRef} className={`${styles.card} ${isRead ? styles.read : ""}`}>
      <div ref={skipBgRef} className={styles.swipeActionSkip}>
        <span className={styles.swipeLabel}>Skip</span>
      </div>
      <div ref={innerRef} className={styles.cardInner}>
        <a href={item.url} target="_blank" rel="noreferrer" className={styles.cardLink}>
          <div className={styles.imageWrap}>
            <img src={item.thumbnail!} alt={item.title} className={styles.image} />
          </div>
          <div className={styles.caption}>
            <span className={styles.captionTitle}>{item.title}</span>
            {item.author && <span className={styles.captionAuthor}>{item.author}</span>}
          </div>
        </a>
        <button
          type="button"
          className={styles.skipBtn}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSkip(); }}
        >
          Skip
        </button>
      </div>
    </div>
  );
}

interface Props {
  snapshot: SourceSnapshot;
  readIds?: Set<string>;
  onRead?: (id: string) => void;
  mobile?: boolean;
}

export function SourcePicturesView({ snapshot, readIds, onRead, mobile = false }: Props) {
  const { hideRead } = useViewSettings();
  const items = snapshot.items.filter((item) => {
    if (!item.thumbnail) return false;
    if (hideRead && readIds?.has(item.id)) return false;
    return true;
  });

  if (items.length === 0) {
    return <div className={styles.empty}>No images in this source.</div>;
  }

  return (
    <div className={`${styles.grid} ${mobile ? styles.mobile : ""}`}>
      {items.map((item) => (
        <PictureCard
          key={item.id}
          item={item}
          isRead={readIds?.has(item.id) ?? false}
          onSkip={() => onRead?.(item.id)}
        />
      ))}
    </div>
  );
}
